define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/store/Memory",
	"csi",
	"csi/def",
	"csi/def/AddPath",
	"csi/def/Action",
	"csi/def/Relationship",
	"csi/def/ViewRef",
	"csi/enums",
	"csi/form/Widget",
	"csi/locale",
	"csi/store/_base",
	"csi/store/Names",
	"csi/xml",
	"csi/xml/Query",
	"Specification/BOM/BOMSectionMixin",
	"Apparel/ProductType"
], function(declare, lang, Memory, csi, def, AddPath, Action, Relationship, ViewRef, enums, FormWidget, locale, csiStore, Names, xml, Query, BOMSectionMixin, ProductType) {

	const FIELD_SECTION = "MaterialMassReplaceSection";
	const FIELD_SCOPE = "MaterialMassReplaceScope";
	const FIELD_COLOR = "MaterialMassReplaceColor";
	const FIELD_FROM = "MaterialMassReplaceFrom";
	const FIELD_TO = "MaterialMassReplaceTo";
	const FIELD_RETAIN = "MaterialMassReplaceRetain";
	const FIELD_THEME_BOM_RETAIN = "ThemeBOMMaterialMassReplaceRetain";
	const FIELD_SYNCH_THEMES = "MaterialMassReplaceSynchThemes";

	const SCOPE_MATERIAL = "Material";
	const SCOPE_COLOR = "Color";
	const SCOPE_COLOR_SPEC = "ColorSpec";
	const SCOPE_COLOR_SPEC_TO_COLOR = "ColorSpecToColor";
	const SCOPE_SIZE = "Size";
	const SCOPE_QUOTE = "Quote";
	const SCOPE_SOURCING_MODEL = "SourcingModel";

	return declare([FormWidget, BOMSectionMixin], {
		idBOMFilter: null,
		/** boolean: indicates whether the action is coming from WhereUsedThemeBOM */
		_isThemeBOMAction: false,
		_storeColorFrom: null,
		_storeColorTo: null,
		_storeColorSpecFrom: null,
		_storeColorSpecTo: null,
		_storeSection: null,
		_storeMaterial: null,
		_storeQuoteFrom: null,
		_storeQuoteTo: null,
		_storeSize: null,
		_storeSourcingModel: null,

		// Boolean: write only visible fields
		bWriteVisible: true,

		getFieldStore: function(/*csi.def.Field*/field, /*String*/value) {
			// summary: if field is for Product or Supplier, add SelectAll option
			const name = field.getControlName();
			if (name == FIELD_SCOPE) {
				let scopeFieldData = [];
				if (this._isThemeBOMAction) {
					scopeFieldData = [
						{id: SCOPE_MATERIAL, name: locale.type("Material")},
						{id: SCOPE_QUOTE, name: locale.type("SupplierItem")}
					];
				} else {
					scopeFieldData = [
						{id: SCOPE_MATERIAL, name: locale.type("Material")},
						{id: SCOPE_COLOR, name: locale.type("ColorMaterial")},
						{id: SCOPE_COLOR_SPEC, name: locale.type("ColorSpecification")},
						{id: SCOPE_COLOR_SPEC_TO_COLOR, name: locale.resource("ColorSpecToColor")},
						{id: SCOPE_SIZE, name: locale.type("ProductSize")},
						{id: SCOPE_QUOTE, name: locale.type("SupplierItem")},
						{id: SCOPE_SOURCING_MODEL, name: locale.typeId("PartMaterial", "SourcingModel")}
					]
				}
				return new Memory({
					data: scopeFieldData
				});
			}

			// From/To fields
			const scope = this.defView.getFieldValue(FIELD_SCOPE);
			if (name == FIELD_COLOR) {
				return this._getColorStoreTo(name);
			} else if (name == FIELD_SECTION) {
				return this._getSectionStore();
			} else if (name == FIELD_FROM) {
				if (scope == SCOPE_COLOR)
					return this._getColorStoreFrom();
				else if ((scope == SCOPE_COLOR_SPEC) ||
					(scope == SCOPE_COLOR_SPEC_TO_COLOR))
					return this._getColorSpecStoreFrom();
				else if (scope == SCOPE_QUOTE)
					return this._getQuoteStoreFrom();
				else if (scope == SCOPE_SIZE)
					return this._getSizeStore();
			} else if (name == FIELD_TO) {
				if (scope == SCOPE_SIZE)
					return this._getSizeStore();
				else if ((scope == SCOPE_COLOR) ||
					(scope == SCOPE_COLOR_SPEC_TO_COLOR))
					return this._getColorStoreTo(name);
				else if (scope == SCOPE_COLOR_SPEC)
					return this._getColorSpecStoreTo();
				else if (scope == SCOPE_QUOTE)
					return this._getQuoteStoreTo();
				else if (scope == SCOPE_SOURCING_MODEL)
					return this._getSourcingModelStore();
				else if (scope == SCOPE_MATERIAL)
					return this._getMaterialStore();
			}

			return this.inherited(arguments);
		},

		onChangeFrom: function(/*csi.def.View*/formView, /*csi.def.Field*/field, /*Any*/value) {
			// summary: when From field changes, reset any PartMaterial selections
			this.defView.m_formData.resetSelections();
		},

		onValidateTo: function(/*csi.def.View*/formView, /*csi.def.Field*/field, /*Any*/value, /*Object*/params) {
			// summary: throw error if from == to
			if ((value == this.getViewURL()) || (value == formView.getFieldValue(FIELD_FROM))) {
				throw new Error(locale.resource("MaterialMassReplaceError_Same"));
			}
		},

		_getSectionStore: function() {
			// summary: get store for BOM section field
			if (!this._storeSection) {
				const actionCtx = {
					isThemeBOMAction: this._isThemeBOMAction,
					isApparelBOMAction: this._isApparelBOMAction,
					isFoodBOMAction: this._isFoodBOMAction,
					isMaterialBOMAction: this._isMaterialBOMAction
				};

				this._storeSection = this.getBOMSectionStore(
					actionCtx,
					this.urlRoot);
			}
			return this._storeSection;
		},

		_getColorStoreFrom: function() {
			// summary: get store for material colors for "from" field
			if (!this._storeColorFrom) {
				const storeFrom = this._getStore(lang.mixin(new Relationship(), {
					m_id: "ProductColors",
					m_url: this.getViewURL()
				}));

				storeFrom.action = lang.mixin(new Action(), {
					m_viewRef: lang.mixin(new ViewRef(), {
						m_id: "ProductColors",
						m_url: this.getViewURL()
					})
				});

				this._storeColorFrom = storeFrom;
			}
			return this._storeColorFrom;
		},

		_getColorStoreTo: function(fieldName) {
			// summary: get store for material colors for "to" field
			if (!this._storeColorTo) {
				this._storeColorTo = {};
			}

			if (!this._storeColorTo[fieldName]) {
				const colorToQry = new Query().Attr("Active", xml.OP_EQUALS, true);

				const storeTo = this._getStore(lang.mixin(new Relationship(), {
					m_url: this.getViewURL(),
					m_id: "ProductColors",
					m_query: colorToQry
				}));

				storeTo.action = lang.mixin(new Action(), {
					m_viewRef: lang.mixin(new ViewRef(), {
						m_id: "ProductColors",
						m_url: this.getViewURL()
					}),
					m_fixedQuery: colorToQry
				});

				this._storeColorTo[fieldName] = storeTo;
			}
			return this._storeColorTo[fieldName];
		},

		_getColorSpecStoreFrom: function() {
			// summary: get store for color specs for "from" field
			if (!this._storeColorSpecFrom) {
				const colorSpecFromQry = new Query();
				colorSpecFromQry.Or()
					.Node(xml.PROP_TYPE, xml.OP_EQUALS, "PrintDesignColor")
					.Node(xml.PROP_TYPE, xml.OP_EQUALS, "ColorSpecification");
				const storeSpecFrom = this._getStore(lang.mixin(new Relationship(), {

					//m_typeNode: "ColorSpecification"
					m_url: this.getViewURL(),
					m_addPaths: [lang.mixin(new AddPath(), {
						m_path: "Parent:Actual/Child:UsedColors"
					})],
					m_sorts: [{id: xml.ATTR_NAME}],
					m_query: colorSpecFromQry
				}));

				storeSpecFrom.action = lang.mixin(new Action(), {
					m_viewRef: lang.mixin(new ViewRef(), {
						m_id: "ColorSpecificationsBOMs",
						m_url: this.getViewURL()
					})
				});

				this._storeColorSpecFrom = storeSpecFrom;
			}

			return this._storeColorSpecFrom;
		},

		_getColorSpecStoreTo: function() {
			// summary: get store for color specs for "to" field
			if (!this._storeColorSpecTo) {
				const colorSpecToQry = new Query()
					.Attr("Active", xml.OP_EQUALS, true)
					.Attr("OKForMaterial", xml.OP_EQUALS, true);
				colorSpecToQry.Or()
					.Node(xml.PROP_TYPE, xml.OP_EQUALS, "PrintDesignColor")
					.And()
					.Node(xml.PROP_TYPE, xml.OP_EQUALS, "ColorSpecification")
					.RefAttr(xml.ATTR_PARENT, xml.OP_EQUALS, csi.EMPTY_URL);

				const storeSpecTo = this._getStore(lang.mixin(new Relationship(), {
					m_query: colorSpecToQry
				}));

				storeSpecTo.action = lang.mixin(new Action(), {
					m_viewRef: lang.mixin(new ViewRef(), {
						m_id: "ProductColorSpecs",
						m_url: "centric://APPAREL/Views"
					}),
					m_fixedQuery: colorSpecToQry
				});

				this._storeColorSpecTo = storeSpecTo;
			}

			return this._storeColorSpecTo;
		},

		_getQuoteStoreFrom: function() {
			// summary: get store for quotes for "from" field
			return this._storeQuoteFrom = this._storeQuoteFrom || this._getStore(lang.mixin(new Relationship(), {
				m_url: this.getViewURL(),
				m_addPaths: [lang.mixin(new AddPath(), {
					m_path: "Child:ProductSourcing/Child:ProductSources/Child:SupplierItems"
				})]
			}));
		},

		_getQuoteStoreTo: function() {
			// summary: get store for quotes for "to" field
			return this._storeQuoteTo = this._storeQuoteTo || this._getStore(lang.mixin(new Relationship(), {
				m_url: this.getViewURL(),
				m_addPaths: [lang.mixin(new AddPath(), {
					m_path: "Child:ProductSourcing/Child:ProductSources/Child:SupplierItems"
				})],
				m_query: new Query()
					.Attr("State", xml.OP_NOT_EQUALS, "Revision.State:ABANDONED", "Child:CurrentRevision")
					.Attr("State", xml.OP_NOT_EQUALS, "Revision.State:CLOSED", "Child:CurrentRevision")
			}));
		},

		_getSizeStore: function() {
			// summary: get store for material sizes
			return this._storeSize = this._storeSize || this._getStore(lang.mixin(new Relationship(), {
				m_url: this.getViewURL(),
				m_id: "ProductSizes"
			}));
		},

		_getSourcingModelStore: function() {
			// summary: get store for sourcing models
			return this._storeSourcingModel = this._storeSourcingModel || enums.getStore("SourcingModel");
		},

		_getMaterialStore: function() {
			// summary: get store for materials
			const material = this.getViewNode();
			let idFilter = this.idBOMFilter;

			if (ProductType.get(material, "MaterialUsage") != "MaterialUsage:Standalone") {
				idFilter = "Usable";
			}

			const materialsQuery = new Query()
				.Node(xml.PROP_URL, xml.OP_NOT_EQUALS, material.$URL)
				.Attr(idFilter, xml.OP_EQUALS, true)
				.Attr("MaterialUsage", xml.OP_EQUALS, "MaterialUsage:Standalone", "Child:ProductType/Child:ProductionConfig");

			// if a BOM section is selected that has a restriction on BOMProductTypes, then apply it
			const selectedSection = this.defView.getFieldValue(FIELD_SECTION);

			this.applyBomSectionQuery(selectedSection, materialsQuery, this.cacheView, false);

			const materialStore = this._getStore(lang.mixin(new Relationship(), {
				m_typeNode: "Material",
				m_query: materialsQuery
			}));

			materialStore.action = lang.mixin(new Action(), {
				m_viewRef: lang.mixin(new ViewRef(), {
					m_id: "MaterialsView",
					m_url: "centric://APPAREL/Views"
				}),
				m_fixedQuery: materialsQuery
			});
			this._storeMaterial = materialStore;

			return this._storeMaterial;
		},

		_getStore: function(/*Relationship*/r) {
			// summary: get store given relationship
			const storeOptions = {data: [csiStore.nameObject("")]};
			csiStore.namesOptionsByRelationship(this.getContext(), r, storeOptions);
			return new Names(storeOptions);
		},

		_updateFieldVisibility: function(/*csi.def.Field?*/changedField) {
			// summary: set display of fields based on scope
			const b = this.inherited(arguments);

			changedField = changedField || this.defView.getField(FIELD_SCOPE);

			if (changedField.getControlName() == FIELD_SCOPE) {
				const toField = this.defView.getField(FIELD_TO);
				const scope = this.defView.getFieldValue(FIELD_SCOPE);
				toField.m_type = (scope == SCOPE_SOURCING_MODEL) ? "enum" : "ref";
				return true;
			}

			return b;
		},

		// Object: map of attributes to expressions indicating if should be hidden
		_hideExpressions: {
			MaterialMassReplaceColor: "attr('MaterialMassReplaceScope') != 'Quote' || attr('MaterialMassReplaceFrom') != 'centric:'",
			MaterialMassReplaceFrom: "attr('MaterialMassReplaceScope') == 'Material' || attr('MaterialMassReplaceColor') != 'centric:'",
			MaterialMassReplaceRetain: "attr('MaterialMassReplaceScope') != 'Material'",
			ThemeBOMMaterialMassReplaceRetain: "attr('MaterialMassReplaceScope') != 'Material'",
			MaterialMassReplaceSynchThemes: "attr('MaterialMassReplaceScope') != 'Material'"
		},

		// Object: map of required attributes expressions - visibility above trumps required status
		_requiredExpressions: {
			MaterialMassReplaceFrom: "attr('MaterialMassReplaceScope') == 'ColorSpecToColor'"
		},

		getFormContext: function() {
			// summary: add form field to node for use by expression
			const ctx = this.inherited(arguments);
			const node = ctx.getNode();
			node.MaterialMassReplaceColor = this.getFieldValue(FIELD_COLOR);
			node.MaterialMassReplaceFrom = this.getFieldValue(FIELD_FROM);
			node.MaterialMassReplaceRetain = this._isThemeBOMAction ? this.getFieldValue(FIELD_THEME_BOM_RETAIN) : this.getFieldValue(FIELD_RETAIN);
			node.MaterialMassReplaceSynchThemes = this.getFieldValue(FIELD_SYNCH_THEMES);
			node.MaterialMassReplaceScope = this.getFieldValue(FIELD_SCOPE);
			return ctx;
		},

		_resolveView: function() {
			const sourceViewId = this.getFormData().getSource().widget.defView.m_id;
			this._isApparelBOMAction = sourceViewId == "WhereUsedApparelBOM";
			this._isFoodBOMAction = sourceViewId == "WhereUsedFoodBOM";
			this._isMaterialBOMAction = sourceViewId == "WhereUsedMaterialBOM";

			//Hidden only for MaterialBOM Tab.
			if (this._isMaterialBOMAction) {
				syncThemesField = this.getFieldByUniqueName(FIELD_SYNCH_THEMES);
				if (syncThemesField) {
					syncThemesField.bHidden = true;
				}
			}

			//Hide Retain field for Theme BOM case and insert ThemeBOMMaterialMassReplaceRetain field instead.
			if (this._isThemeBOMAction) {
				const retainField = this.getFieldByUniqueName(FIELD_RETAIN);
				retainField.bHidden = true;
				this.insertFieldIfNotExist(FIELD_THEME_BOM_RETAIN, FIELD_SYNCH_THEMES, {
					m_typeControl: def.FIELD_CHECKBOX,
				});
				this.addDefaultValue(FIELD_THEME_BOM_RETAIN, true);
				const syncThemesField = this.getFieldByUniqueName(FIELD_SYNCH_THEMES);
				syncThemesField.bHidden = true;
			} else {
				this.addDefaultValue(FIELD_RETAIN, true);
			}

			// summary: set default values
			this.addDefaultValue(FIELD_SCOPE, SCOPE_MATERIAL);
			this.inherited(arguments);
		}
	});
});